import { ComponentType, lazy, LazyExoticComponent } from 'react';

export const CHUNK_RELOAD_KEY = 'ms-chunk-reload';

type ModuleDefault<T> = { default: T };

/** Preserve props from a default-exported component without using `any`. */
type AsComponentType<T> = T extends ComponentType<infer P>
  ? ComponentType<P>
  : ComponentType;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String(error);
  const name = error instanceof Error ? error.name : '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

/** Shared by React.lazy wrapper and ChunkErrorBoundary. */
export function reloadOnceForStaleChunk(): boolean {
  const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
  if (alreadyReloaded) {
    return false;
  }
  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  window.location.reload();
  return true;
}

export async function importWithRetry<T>(
  factory: () => Promise<ModuleDefault<T>>
): Promise<ModuleDefault<T>> {
  try {
    const mod = await factory();
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    return mod;
  } catch (error) {
    if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
      // Never resolves; page is reloading.
      return new Promise(() => undefined);
    }
    throw error;
  }
}

/**
 * Like React.lazy, but on a failed dynamic import (stale chunk after deploy)
 * reloads the page once so the browser picks up the new index.html.
 */
export function lazyWithRetry<T>(
  factory: () => Promise<ModuleDefault<T>>
): LazyExoticComponent<AsComponentType<T>> {
  return lazy(
    () => importWithRetry(factory) as Promise<{ default: ComponentType }>
  ) as LazyExoticComponent<AsComponentType<T>>;
}
